import { Box, Button, Icon, Typography, ButtonProps } from '@drone-js/core';
import { useWalletContext } from '@v2/contexts/wallet/hooks/useWalletContext';
import React from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { OtpInputBoxes } from '../OtpForm/OtpInputBoxes';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { WalletOtpFormCmsWithDefault } from './WalletOtpFormCmsWithDefault';
import { StringFormatter } from '@utils/stringFormatter';
import { ResendOtpWithTimer } from './ResendOtpWithTimer';
import { MFAOperationCode } from '@ul/common';
import { useAnalytics } from './analytics';
import styled from 'styled-components';

const PreWrapTypography = styled(Typography)({
    whiteSpace: 'pre-wrap',
});

const WalletOtpForm: React.FC = () => {
    const [otpCode, setOtpCode] = React.useState<string | null>('');
    const [buttonState, setButtonState] = React.useState<ButtonProps['state']>('disabled');

    const { AnalyticsOtpFormProps, AnalyticsSubmitButtonProps } = useAnalytics();

    const translation = useTranslation(WalletOtpFormCmsWithDefault);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const { walletStorage, onSubmit, onResendOtp } = useWalletContext();
    const { otpVm, dob, transactionInfo, isBlocked } = useStore(
        walletStorage,
        useShallow(state => ({
            otpVm: state.otpVm,
            dob: state.dob,
            transactionInfo: state.transactionInfo,
            isBlocked: state.isBlocked,
        }))
    );

    const onButtonClick = async () => {
        setButtonState('loading');
        await onSubmit({ dob, otpVerifyInfo: { ...otpVm, code: otpCode } });
        setButtonState('disabled');
    };

    React.useEffect(() => {
        if (inputRef.current) inputRef.current.pattern = '[0-9]+';
    }, [inputRef]);

    React.useEffect(() => {
        if (otpCode.length === 6 && !isBlocked) {
            setButtonState('enabled');
        } else setButtonState('disabled');
    }, [otpCode, isBlocked]);

    const isAmountAndCurrencyExist = () => {
        return !!(transactionInfo.amount && transactionInfo.currency);
    };

    const isStatement = () => {
        return !!(transactionInfo?.mfaOperationCode === MFAOperationCode.WalletStatement);
    };

    const getTitle = () => {
        if (transactionInfo?.mfaOperationCode && (isAmountAndCurrencyExist() || isStatement())) {
            switch (transactionInfo.mfaOperationCode) {
                case MFAOperationCode.SCAWithdraw:
                    return StringFormatter.format(
                        translation.WalletWithdrawTitle,
                        transactionInfo.currency,
                        transactionInfo.amount
                    );
                case MFAOperationCode.PayWithWallet:
                    return StringFormatter.format(
                        translation.WalletPaymentTitle,
                        transactionInfo.currency,
                        transactionInfo.amount
                    );
                case MFAOperationCode.WalletStatement:
                    return translation.WalletStatementTitle;
            }
        }
        return translation.WalletOtpTitle;
    };

    return (
        <Box data-cy="wallet-otp-form" {...AnalyticsOtpFormProps}>
            <Box mt="16" mb="12" display="flex" justifyContent="center" alignItems="center">
                <Box
                    width=""
                    display="flex"
                    p="12"
                    borderRadius="12"
                    borderStyle="solid"
                    borderWidth="1"
                    borderColor="base.subtle"
                    backgroundColor="base.background"
                    justifyContent="center"
                >
                    <Icon id="fill.symbol.envelope" size="24" color="product.primary" />
                </Box>
            </Box>
            <Typography textAlign="center" mb="8" variant="heading-xl" fontWeight={600}>
                {getTitle()}
            </Typography>
            {otpVm?.refCode && (
                <Typography
                    data-cy="wallet-otp-refcode"
                    mb="8"
                    textAlign="center"
                    variant="body-s"
                    textColor="base.subtle"
                    fontWeight={400}
                >
                    Ref #{otpVm.refCode}
                </Typography>
            )}
            <PreWrapTypography data-cy="otpTarget" textAlign="center" mb="24" variant="body-s" fontWeight={400}>
                {StringFormatter.format(translation.WalletOtpCaption, '\n' + otpVm.target)}
            </PreWrapTypography>
            <OtpInputBoxes setOtpValue={setOtpCode} />
            <ResendOtpWithTimer
                translation={{ didntReceive: translation.DidntReceive, resendCode: translation.ResendCode }}
                onClick={onResendOtp}
            />
            <Button
                {...AnalyticsSubmitButtonProps}
                mt="16"
                hierarchy="primary"
                width="parent-fit"
                variant="label"
                label="Submit"
                data-cy="wallet-otp-submit-button"
                state={buttonState}
                onClick={onButtonClick}
            />
        </Box>
    );
};

export { WalletOtpForm };
